/**
 * AskX1Markdown.tsx — minimal, SAFE Markdown renderer for Ask X1 replies.
 *
 * Why hand-rolled: no markdown lib in the repo, and reply text is influenced by
 * untrusted on-chain data (token names, memos). This renders to React nodes only
 * — NO dangerouslySetInnerHTML, no raw HTML — so injection is impossible. It also
 * truncates long base58 addresses (32–88 chars) to 4…4 in every displayed text
 * segment and href-whitelists links (only http(s) or internal /paths).
 *
 * Supports: headings (1-6), bold, italic, inline code, bullet and numbered
 * lists, links, paragraphs, and line breaks.
 */
import { Fragment, type ReactNode } from 'react'

const ADDR = /[1-9A-HJ-NP-Za-km-z]{32,88}/g
function truncAddrs(t: string): string {
  return t.replace(ADDR, s => `${s.slice(0, 4)}…${s.slice(-4)}`)
}

// Only allow internal (/...) or http(s) links — reject javascript:, data:, etc.
function safeHref(href: string): string | null {
  const h = href.trim()
  if (/^https?:\/\//i.test(h)) return h
  if (h.startsWith('/')) return h
  return null
}

const INLINE = /(\*\*|__)(.+?)\1|(\*|_)(.+?)\3|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)/g

function renderInline(text: string, kp: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let k = 0
  let m: RegExpExecArray | null
  INLINE.lastIndex = 0
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(<Fragment key={`${kp}t${k}`}>{truncAddrs(text.slice(last, m.index))}</Fragment>)
    if (m[1]) {
      out.push(<strong key={`${kp}b${k}`}>{truncAddrs(m[2])}</strong>)
    } else if (m[3]) {
      out.push(<em key={`${kp}i${k}`}>{truncAddrs(m[4])}</em>)
    } else if (m[5] != null) {
      out.push(<code key={`${kp}c${k}`} className="rounded bg-black/10 px-1 py-0.5 text-[0.85em] dark:bg-white/10">{truncAddrs(m[5])}</code>)
    } else if (m[6] != null) {
      const href = safeHref(m[7])
      const label = truncAddrs(m[6])
      out.push(
        href
          ? <a key={`${kp}a${k}`} href={href} className="text-primary-600 underline dark:text-primary-400"
               {...(/^https?:/i.test(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{label}</a>
          : <Fragment key={`${kp}a${k}`}>{label}</Fragment>,
      )
    }
    last = INLINE.lastIndex
    k++
  }
  if (last < text.length) out.push(<Fragment key={`${kp}t${k}`}>{truncAddrs(text.slice(last))}</Fragment>)
  return out
}

const HEADING = /^(#{1,6})\s+(.*)$/
const LIST_ITEM = /^\s*([-*]|\d+\.)\s+/

export function AskX1Markdown({ text }: { text: string }) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let i = 0
  let key = 0
  while (i < lines.length) {
    const line = lines[i]
    const h = HEADING.exec(line)
    if (h) {
      const lvl = h[1].length
      const cls = lvl <= 1 ? 'text-base font-semibold' : lvl === 2 ? 'text-sm font-semibold' : 'text-sm font-medium'
      blocks.push(<div key={`h${key}`} className={`${cls} mt-1`}>{renderInline(h[2], `h${key}`)}</div>)
      key++; i++; continue
    }
    if (LIST_ITEM.test(line)) {
      const ordered = /^\s*\d+\.\s+/.test(line)
      const items: ReactNode[] = []
      let j = 0
      while (i < lines.length && LIST_ITEM.test(lines[i])) {
        const item = lines[i].replace(LIST_ITEM, '')
        items.push(<li key={`li${key}-${j}`}>{renderInline(item, `li${key}${j}`)}</li>)
        i++; j++
      }
      blocks.push(
        ordered
          ? <ol key={`l${key}`} className="my-1 ml-5 list-decimal space-y-0.5">{items}</ol>
          : <ul key={`l${key}`} className="my-1 ml-5 list-disc space-y-0.5">{items}</ul>,
      )
      key++; continue
    }
    if (line.trim() === '') { i++; continue }
    const para: string[] = []
    while (i < lines.length && lines[i].trim() !== '' && !HEADING.test(lines[i]) && !LIST_ITEM.test(lines[i])) {
      para.push(lines[i]); i++
    }
    blocks.push(<p key={`p${key}`} className="my-0.5">{renderInline(para.join(' '), `p${key}`)}</p>)
    key++
  }
  return <div className="space-y-1 leading-relaxed">{blocks}</div>
}
